import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
  CmmdProjectMilestone,
  CmmdTaskStatus,
  CmmdTaskWorkflow,
  CmmdWorkItemType,
  UpdateCmmdBoardTaskParams,
} from "@cmmd-forge/contracts";
import type { ReactNode } from "react";
import { ProjectTaskPicker, type ProjectTaskPickerOption } from "./ProjectTaskPicker";
import { WORK_ITEM_TYPE_OPTIONS, taskWorkItemTypeLabel } from "./atoms/TaskWorkItemTypeIcon";
import { normalizeTaskStatus } from "./taskViewTypes";
import { projectTaskStatusOptions } from "./projectTaskStatusOptions";
import { NO_ZOOM_FIELD_TEXT_CLASS } from "~/lib/touchTarget";
import { ProjectTaskPeopleFields } from "./ProjectTaskPeopleFields";
import type { AssignmentOperation } from "./projectTaskAssignmentSelection";

type Priority = NonNullable<UpdateCmmdBoardTaskParams["priority"]>;

const PRIORITY_OPTIONS: readonly Priority[] = ["low", "medium", "high", "urgent"];
const FIELD = `min-h-touch w-full rounded-lg border border-transparent bg-transparent px-3 ${NO_ZOOM_FIELD_TEXT_CLASS} text-foreground outline-none transition-colors hover:border-border hover:bg-muted focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 lg:h-8 lg:min-h-0 lg:rounded-md lg:px-2`;

export interface ProjectTaskEditableDetailsProps {
  readonly task: CmmdBoardTask;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly members: readonly CmmdMember[];
  readonly agentMembers?: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading?: boolean;
  readonly agentMembersError?: string | null;
  readonly milestones: readonly CmmdProjectMilestone[];
  readonly pending: boolean;
  readonly statusMutationDisabled: boolean;
  readonly workflow: CmmdTaskWorkflow | null;
  readonly onSetStatus: (id: number, status: CmmdTaskStatus, workflowStageId?: string) => void;
  readonly onUpdate: (id: number, patch: UpdateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetAssignment: (
    id: number,
    subject: CmmdProjectTaskAssignmentSubject,
    operation?: AssignmentOperation,
  ) => Promise<boolean>;
}

export function ProjectTaskEditableDetails(props: ProjectTaskEditableDetailsProps) {
  const parentOptions = availableParentTasks(props.task, props.allTasks);
  const agentMembers = props.agentMembers ?? EMPTY_AGENT_MEMBERS;
  return (
    <dl className="space-y-1">
      <EditableDetail label="Status">
        <ProjectTaskPicker
          ariaLabel="Status"
          presentation="quietField"
          value={props.task.workflowStageId ?? normalizeTaskStatus(props.task.status)}
          emptyLabel="Todo"
          allowEmpty={props.workflow == null}
          options={projectTaskStatusOptions(props.task, props.workflow)}
          disabled={props.pending || props.statusMutationDisabled}
          onValueChange={(value) => {
            const stage = props.workflow?.stages.find((entry) => entry.id === value);
            props.onSetStatus(
              props.task.id,
              stage?.category ?? ((value ?? "todo") as CmmdTaskStatus),
              stage?.id,
            );
          }}
        />
      </EditableDetail>
      <ProjectTaskPeopleFields
        task={props.task}
        members={props.members}
        agentMembers={agentMembers}
        agentMembersLoading={props.agentMembersLoading}
        agentMembersError={props.agentMembersError}
        pending={props.pending}
        detail={EditableDetail}
        onUpdate={props.onUpdate}
        onSetAssignment={props.onSetAssignment}
      />
      <EditableDetail label="Priority">
        <ProjectTaskPicker
          ariaLabel="Priority"
          presentation="quietField"
          value={props.task.priority}
          emptyLabel="Medium"
          options={priorityOptions()}
          disabled={props.pending}
          onValueChange={(value) =>
            void props.onUpdate(props.task.id, {
              priority: (value ?? "medium") as Priority,
            })
          }
        />
      </EditableDetail>
      <EditableDetail label="Work item type">
        <select
          aria-label="Work item type"
          value={props.task.workItemType}
          disabled={props.pending}
          onChange={(event) =>
            void props.onUpdate(props.task.id, {
              workItemType: event.currentTarget.value as CmmdWorkItemType,
            })
          }
          className={FIELD}
        >
          {WORK_ITEM_TYPE_OPTIONS.map((workItemType) => (
            <option key={workItemType} value={workItemType}>
              {taskWorkItemTypeLabel(workItemType)}
            </option>
          ))}
        </select>
      </EditableDetail>
      <EditableDetail label="Due date">
        <input
          type="date"
          aria-label="Due date"
          value={dateInputValue(props.task.dueDate)}
          disabled={props.pending}
          onChange={(event) =>
            void props.onUpdate(props.task.id, {
              dueDate: event.currentTarget.value || null,
            })
          }
          className={FIELD}
        />
      </EditableDetail>
      <EditableDetail label="Parent">
        <ProjectTaskPicker
          ariaLabel="Parent task"
          presentation="quietField"
          value={optionalString(props.task.parentTaskId)}
          emptyLabel="None"
          options={parentPickerOptions(props.task, parentOptions)}
          disabled={props.pending}
          onValueChange={(value) =>
            void props.onUpdate(props.task.id, {
              parentTaskId: optionalNumber(value),
            })
          }
        />
      </EditableDetail>
      <EditableDetail label="Milestone">
        <ProjectTaskPicker
          ariaLabel="Milestone"
          presentation="quietField"
          value={optionalString(props.task.milestoneId)}
          emptyLabel="None"
          options={milestoneOptions(props.task, props.milestones)}
          disabled={props.pending}
          onValueChange={(value) =>
            void props.onUpdate(props.task.id, {
              milestoneId: optionalNumber(value),
            })
          }
        />
      </EditableDetail>
    </dl>
  );
}

const EMPTY_AGENT_MEMBERS: readonly CmmdProjectAgentMember[] = [];

function EditableDetail(props: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="group/quiet-detail grid grid-cols-[6rem_minmax(0,1fr)] items-center gap-2 py-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{props.label}</dt>
      <dd className="min-w-0">{props.children}</dd>
    </div>
  );
}

function priorityOptions(): readonly ProjectTaskPickerOption[] {
  return PRIORITY_OPTIONS.map<ProjectTaskPickerOption>((priority) => ({
    value: priority,
    label: capitalize(priority),
    visual: { kind: "priority", priority },
  }));
}

function availableParentTasks(
  task: CmmdBoardTask,
  allTasks: readonly CmmdBoardTask[],
): readonly CmmdBoardTask[] {
  const blockedIds = descendantIds(task);
  return allTasks.filter((candidate) => candidate.id !== task.id && !blockedIds.has(candidate.id));
}

function parentPickerOptions(
  task: CmmdBoardTask,
  candidates: readonly CmmdBoardTask[],
): readonly ProjectTaskPickerOption[] {
  const options = candidates.map((candidate) => ({
    value: String(candidate.id),
    label: `#${candidate.id} ${candidate.title}`,
  }));
  if (task.parentTaskId === null || candidates.some(({ id }) => id === task.parentTaskId)) {
    return options;
  }
  return [{ value: String(task.parentTaskId), label: `#${task.parentTaskId}` }, ...options];
}

function milestoneOptions(
  task: CmmdBoardTask,
  milestones: readonly CmmdProjectMilestone[],
): readonly ProjectTaskPickerOption[] {
  const options = milestones.map((milestone) => ({
    value: String(milestone.id),
    label: milestone.title,
  }));
  if (task.milestoneId === null || milestones.some(({ id }) => id === task.milestoneId)) {
    return options;
  }
  return [
    {
      value: String(task.milestoneId),
      label: `Milestone #${task.milestoneId}`,
    },
    ...options,
  ];
}

function descendantIds(task: CmmdBoardTask): ReadonlySet<number> {
  const ids = new Set<number>();
  const visit = (candidate: CmmdBoardTask) => {
    for (const child of candidate.children) {
      ids.add(child.id);
      visit(child);
    }
  };
  visit(task);
  return ids;
}

function optionalString(value: number | null): string | null {
  return value === null ? null : String(value);
}

function optionalNumber(value: string | null): number | null {
  return value === null ? null : Number(value);
}

function dateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : "";
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
